import Bugsnag from '@bugsnag/js';
import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { ProjectFeature } from '@civilgrid/utils/src/common/featureAccess';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSnapshot } from 'valtio';
import { projectConnectbaseTasksQueryKey } from '~/api/projectConnectbaseTasks';
import { hasFeatureAccess } from '~/authorizations/FeatureFlags';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { MIN_WIDTH } from '~/components2/Splitter';
import rootStore from '~/store';
import type { CustomLayoutProps, ProjectDetailsParams } from '..';
import { ConnectbaseNotice } from '../connectbaseStatus/ConnectbaseNotice';
import { pickUtilitiesBody, resolveAgenciesNotice } from '../connectbaseStatus/status';
import { useConnectbaseStatus } from '../connectbaseStatus/useConnectbaseStatus';
import projectDetailsStore from '../store';
import Drawers from './drawers';
import {
  POLL_INTERVAL_MS,
  resolvePollOutcome,
  shouldFireSlowLoadSignal,
  type UtilitiesLoadOutcome,
} from './loadTelemetry';
import Stats from './Stats';
import store from './store';
import Table from './Table';
import TopNav from './TopNav';

export function MapChildren() {
  return null;
}

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { isLoading, isError, project } = useSnapshot(store) as typeof store;
  const { isExpanded } = useSnapshot(projectDetailsStore);
  const { currentOrganization } = useSnapshot(rootStore) as typeof rootStore;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInitializedRef = useRef(false);
  const pollAttemptsRef = useRef(0);
  const pollStartRef = useRef<number | null>(null);
  const slowLoadFiredRef = useRef(false);
  const isProjectAgenciesJobEnabled = useFeatureFlagEnabled('project-agencies-job');
  // The store can still hold the previous project after navigation.
  const agenciesTaskStatus = project?.id === projectId ? project.projectAgenciesTaskStatus : undefined;
  const connectbase = useConnectbaseStatus(projectId);
  // The legacy path has no agencies task status.
  const agenciesNotice = isProjectAgenciesJobEnabled
    ? resolveAgenciesNotice(agenciesTaskStatus, connectbase.status.agencies)
    : null;

  const queryClient = useQueryClient();
  // The ConnectBase row is written after the agencies status, so it's re-read on every poll tick, not once per change.
  const refreshConnectbaseStatus = useCallback(
    () => queryClient.refetchQueries({ queryKey: projectConnectbaseTasksQueryKey(projectId) }),
    [queryClient, projectId],
  );

  const removeTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const reportPollOutcome = useCallback(
    (outcome: UtilitiesLoadOutcome, taskStatus: string | undefined) => {
      const durationMs = Math.round(performance.now() - (pollStartRef.current ?? performance.now()));
      captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesTabLoaded, {
        project_id: projectId,
        load_phase: 'job_poll',
        outcome,
        duration_ms: durationMs,
        poll_attempts: pollAttemptsRef.current,
        task_status: taskStatus,
      });
      if (outcome !== 'completed') {
        Bugsnag.notify(new Error(`Utilities tab agency population ${outcome}`), (event) => {
          event.addMetadata('utilitiesTab', {
            projectId,
            outcome,
            taskStatus,
            durationMs,
            pollAttempts: pollAttemptsRef.current,
          });
        });
      }
    },
    [projectId],
  );

  const pollProject = useCallback(async () => {
    try {
      await store.fetchProject(projectId);
      const status = store.project?.projectAgenciesTaskStatus;
      const outcome = resolvePollOutcome(status, pollAttemptsRef.current);

      if (outcome === 'completed') {
        removeTimeout();
        reportPollOutcome('completed', status);
        store.fetchData(projectId);
        return true;
      }

      if (outcome === 'failed' || outcome === 'timed_out') {
        removeTimeout();
        reportPollOutcome(outcome, status);
        // Settle the status first so the error state renders with it, not the generic message.
        await refreshConnectbaseStatus();
        store.isLoading = false;
        store.isError = true;
        return false;
      }

      const elapsedMs = performance.now() - (pollStartRef.current ?? performance.now());
      if (shouldFireSlowLoadSignal(elapsedMs, slowLoadFiredRef.current)) {
        slowLoadFiredRef.current = true;
        captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesTabSlowLoadDetected, {
          project_id: projectId,
          load_phase: 'job_poll',
          elapsed_ms: Math.round(elapsedMs),
          poll_attempts: pollAttemptsRef.current,
          task_status: status,
        });
      }

      void refreshConnectbaseStatus();
      pollAttemptsRef.current += 1;
      timeoutRef.current = setTimeout(pollProject, POLL_INTERVAL_MS);
      return false;
    } catch (_e) {
      removeTimeout();
      reportPollOutcome('failed', 'fetch_error');
      store.isLoading = false;
      store.isError = true;
      return false;
    }
  }, [projectId, removeTimeout, reportPollOutcome, refreshConnectbaseStatus]);

  // New path: poll based on projectAgenciesTaskStatus
  useEffect(() => {
    if (!isProjectAgenciesJobEnabled) return;
    if (!project?.projectAgenciesTaskStatus || hasInitializedRef.current) {
      return;
    }
    // Guard against stale project data in the snapshot from a previous navigation.
    // The effect fires when projectId changes, but the store may still hold the old project.
    if (project.id !== projectId) return;

    // Make sure we only run this hook one time since pollProject will update the projectAgenciesStatus
    hasInitializedRef.current = true;

    if (project.projectAgenciesTaskStatus === 'notStarted') {
      (async () => {
        try {
          pollStartRef.current = performance.now();
          slowLoadFiredRef.current = false;
          store.isLoading = true;
          await store.populateAgencies(projectId);
          pollProject();
        } catch (_e) {
          reportPollOutcome('failed', 'populate_error');
          store.isLoading = false;
          store.isError = true;
          hasInitializedRef.current = false;
        }
      })();
    } else if (project.projectAgenciesTaskStatus === 'completed') {
      store.fetchData(projectId);
    } else if (project.projectAgenciesTaskStatus === 'failed') {
      reportPollOutcome('failed', 'failed');
      store.isLoading = false;
      store.isError = true;
    } else {
      pollStartRef.current = performance.now();
      slowLoadFiredRef.current = false;
      store.isLoading = true;
      pollProject();
    }
  }, [
    projectId,
    project?.projectAgenciesTaskStatus,
    isProjectAgenciesJobEnabled,
    pollProject,
    project?.id,
    reportPollOutcome,
  ]);

  // Legacy path: fetch all data in a single call
  useEffect(() => {
    if (!isProjectAgenciesJobEnabled) {
      store.fetchDataLegacy(projectId);
    }
    hasInitializedRef.current = false;

    return () => {
      hasInitializedRef.current = false;
      pollAttemptsRef.current = 0;
      pollStartRef.current = null;
      slowLoadFiredRef.current = false;
      removeTimeout();
    };
  }, [projectId, isProjectAgenciesJobEnabled, removeTimeout]);

  const infoPanel = useMemo(() => {
    if (
      project &&
      hasFeatureAccess(ProjectFeature.Digitization, project.billingTier, currentOrganization?.subscription)
    ) {
      return (
        <span className="mb-2 block rounded-lg bg-gray-100 p-4 text-sm">
          🚀{' '}
          <a
            href="https://civilgrid.help.usepylon.com/articles/2688571974-digitization"
            target="_blank"
            rel="noreferrer noopener"
            className="text-blue-50 underline hover:text-blue-60"
          >
            Full digitization
          </a>{' '}
          is included for this project. A CivilGrid rep will reach out to finalize scope when we're ready to begin
          digitization. If you have any questions, feel free to{' '}
          <Link
            to="/projects/$projectId/$tabId"
            params={{ projectId, tabId: 'utilities' }}
            search={{ drawer: 'project-channel' }}
            className="text-blue-50 underline hover:text-blue-60"
          >
            start a chat anytime
          </Link>
          .
        </span>
      );
    }
    return null;
  }, [project, projectId, currentOrganization?.subscription]);

  const body = pickUtilitiesBody({
    isLoading,
    isError,
    notice: agenciesNotice,
    statusPending: connectbase.isFetching,
  });

  // One root, since CustomLayout spreads its flex children apart; min-h-0 lets the table scroll inside it.
  return (
    <div className="flex min-h-0 flex-col">
      {/* Mounted empty and filled in later, so screen readers announce the notice. */}
      {/* biome-ignore lint/a11y/useSemanticElements: <output> is for form results, not a status message */}
      <div role="status" data-testid="utilities-connectbase-status">
        {body === 'notice' && agenciesNotice && (
          <div className="px-4 pt-2">
            <ConnectbaseNotice projectId={projectId} status={agenciesNotice} />
          </div>
        )}
      </div>
      {body === 'spinner' && (
        <div className="px-4">
          <SkeletonSpinnerCentered />
        </div>
      )}
      {body === 'error' && (
        <div className="text-center text-sm text-gray-70">An error occurred when fetching utility data.</div>
      )}
      {body === 'content' && (
        <div className="relative overflow-auto px-4">
          {infoPanel}
          {!isExpanded && <Stats />}
          {/* Keyed by project so search, filters and sort start fresh on each project. */}
          <Table key={projectId} />
        </div>
      )}
    </div>
  );
}

export function CustomLayout({ className = '', children }: CustomLayoutProps) {
  const { isExpanded } = useSnapshot(projectDetailsStore);
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const isProjectAgenciesJobEnabled = useFeatureFlagEnabled('project-agencies-job');

  useEffect(() => {
    if (isProjectAgenciesJobEnabled === undefined) return;
    if (isProjectAgenciesJobEnabled) {
      store.fetchProject(projectId);
    } else {
      store.fetchDataLegacy(projectId);
    }
  }, [projectId, isProjectAgenciesJobEnabled]);

  return (
    <div className={`flex flex-col justify-between border-b ${className}`}>
      <TopNav />

      <div className="relative flex h-full grow overflow-hidden">
        <div className="grow">{children}</div>
        <section
          className="flex h-full flex-col justify-between space-y-4 overflow-clip border-l border-grey-30 py-4"
          id={`tab-utilities-open`}
          aria-labelledby="utilities-top-nav-title"
          style={{ width: isExpanded ? '100%' : MIN_WIDTH }}
        >
          <TabDetails />
        </section>

        <Drawers />
      </div>
    </div>
  );
}
